import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, ChevronRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { ROUTE_PATHS } from "@/lib/index";
import { springPresets } from "@/lib/motion";

const LAST_UPDATED = "16 kwietnia 2026 r.";
const ADMIN_NAME   = "e-grób sp. z o.o. (w organizacji)";
const ADMIN_EMAIL  = "kontakt@e-grob.pl";

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <motion.section id={id}
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={springPresets.gentle}
      className="mb-10">
      <h2 className="text-lg font-semibold mb-4 pb-2"
        style={{ fontFamily: "'Playfair Display', serif", color: "rgba(212,175,55,0.75)", borderBottom: "1px solid rgba(212,175,55,0.12)" }}>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed" style={{ color: "rgba(165,180,200,0.8)" }}>
        {children}
      </div>
    </motion.section>
  );
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <ChevronRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "rgba(212,175,55,0.4)" }} />
      <span>{children}</span>
    </li>
  );
}

export default function TermsPage() {
  return (
    <Layout>
      <div className="min-h-screen py-24 px-4"
        style={{ background: "linear-gradient(180deg, oklch(0.09 0.015 235) 0%, oklch(0.11 0.025 225) 100%)" }}>
        <div className="container mx-auto max-w-2xl">

          {/* Nagłówek */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle} className="mb-12 text-center">
            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.2)" }}>
                <FileText className="w-6 h-6" style={{ color: "rgba(212,175,55,0.7)" }} />
              </div>
            </div>
            <p className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: "rgba(140,155,175,0.5)" }}>
              Dokument prawny
            </p>
            <h1 className="text-3xl font-bold mb-3"
              style={{ fontFamily: "'Playfair Display', serif", color: "rgba(212,175,55,0.78)" }}>
              Regulamin platformy e-grób
            </h1>
            <p className="text-xs" style={{ color: "rgba(120,135,155,0.55)" }}>
              Ostatnia aktualizacja: {LAST_UPDATED}
            </p>
            <div className="mt-4 p-4 rounded-xl text-xs text-left"
              style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.15)" }}>
              <p style={{ color: "rgba(165,180,200,0.75)" }}>
                Niniejszy Regulamin określa zasady korzystania z platformy <strong style={{ color: "rgba(212,175,55,0.7)" }}>e-grób</strong> i stanowi umowę pomiędzy Usługodawcą a Użytkownikiem w rozumieniu ustawy z dnia 18 lipca 2002 r. o świadczeniu usług drogą elektroniczną (Dz.U. 2002 nr 144 poz. 1204 z późn. zm.) oraz ustawy z dnia 30 maja 2014 r. o prawach konsumenta (Dz.U. 2014 poz. 827 z późn. zm.).
              </p>
            </div>
          </motion.div>

          {/* § 1 */}
          <Section id="definicje" title="§ 1. Definicje">
            <ul className="space-y-2">
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Platforma / Serwis</strong> — serwis internetowy e-grób dostępny pod domeną e-grob.pl oraz aplikacja webowa.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Usługodawca</strong> — {ADMIN_NAME}, administrator Platformy.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Użytkownik</strong> — osoba fizyczna, która ukończyła 16 lat, posiadająca pełną zdolność do czynności prawnych lub działająca za zgodą opiekuna prawnego, korzystająca z Platformy po rejestracji.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>e-grób</strong> — cyfrowe miejsce pamięci tworzone przez Użytkownika na Platformie, poświęcone osobie zmarłej.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Treści</strong> — wszelkie materiały przesyłane przez Użytkownika: tekst, zdjęcia, pliki graficzne, dokumenty, nagrania.</Li>
              <Li><strong style={{ color: "rgba(210,225,240,0.85)" }}>Plan subskrypcji</strong> — wybrany przez Użytkownika wariant dostępu (Free, Podstawowy, Premium).</Li>
            </ul>
          </Section>

          {/* § 2 */}
          <Section id="rejestracja" title="§ 2. Rejestracja i zawarcie umowy">
            <ul className="space-y-2">
              <Li>Korzystanie z pełnych funkcji Platformy wymaga założenia konta poprzez podanie imienia, nazwiska i adresu e-mail oraz ustalenia hasła.</Li>
              <Li>Umowa o świadczenie usług drogą elektroniczną zostaje zawarta z chwilą potwierdzenia rejestracji przez kliknięcie linku aktywacyjnego wysłanego na podany adres e-mail.</Li>
              <Li>Użytkownik zobowiązany jest do podania prawdziwych i aktualnych danych. Podanie fałszywych danych stanowi podstawę do rozwiązania umowy.</Li>
              <Li>Jeden Użytkownik może posiadać jedno konto. Tworzenie kont wielokrotnych jest zabronione.</Li>
              <Li>Konto jest niezbywalne — nie można go przenosić na inne osoby.</Li>
            </ul>
          </Section>

          {/* § 3 */}
          <Section id="uslugi" title="§ 3. Zakres i plany usług">
            <p>Platforma udostępnia trzy plany subskrypcji:</p>
            <div className="mt-3 space-y-3">
              {[
                { name: "Free", color: "rgba(130,150,175,0.85)", storage: "500 MB", desc: "1 e-grób, podstawowe elementy dekoracyjne, publiczny profil." },
                { name: "Podstawowy", color: "rgba(100,180,220,0.85)", storage: "5 GB", desc: "3 e-groby, rozszerzone elementy, możliwość ustawienia prywatności, wsparcie e-mail." },
                { name: "Premium", color: "rgba(212,175,55,0.85)", storage: "50 GB", desc: "Nieograniczona liczba e-grobów, wszystkie elementy premium, priorytetowe wsparcie, eksport danych." },
              ].map(p => (
                <div key={p.name} className="p-4 rounded-xl"
                  style={{ background: "oklch(0.10 0.022 220)", border: `1px solid ${p.color}30` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold" style={{ color: p.color }}>{p.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: `${p.color}15`, color: p.color, border: `1px solid ${p.color}30` }}>
                      {p.storage}
                    </span>
                  </div>
                  <p className="text-xs">{p.desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs" style={{ color: "rgba(130,145,165,0.6)" }}>
              Aktualny cennik planów płatnych dostępny jest na stronie głównej Platformy. Usługodawca zastrzega prawo do zmiany cen z 30-dniowym wyprzedzeniem.
            </p>
          </Section>

          {/* § 4 */}
          <Section id="tresci" title="§ 4. Treści publikowane przez Użytkownika">
            <ul className="space-y-2">
              <Li>Użytkownik ponosi pełną odpowiedzialność za przesyłane Treści. Publikując je, oświadcza, że posiada do nich prawa autorskie lub stosowne zgody.</Li>
              <Li>Zabrania się publikowania treści: sprzecznych z prawem polskim i europejskim, naruszających dobra osobiste osób trzecich, pornograficznych, szerzących nienawiść, naruszających prawa autorskie.</Li>
              <Li>Użytkownik udziela Usługodawcy niewyłącznej, nieodpłatnej licencji na przechowywanie i wyświetlanie Treści wyłącznie w celu świadczenia usługi.</Li>
              <Li>Usługodawca może usunąć Treści naruszające Regulamin bez wcześniejszego ostrzeżenia i ma prawo do zawieszenia lub usunięcia konta.</Li>
              <Li>Usługodawca nie weryfikuje Treści przed ich publikacją (model notice-and-takedown).</Li>
            </ul>
          </Section>

          {/* § 5 */}
          <Section id="platnosci" title="§ 5. Płatności i subskrypcje">
            <ul className="space-y-2">
              <Li>Plan Free jest bezpłatny bezterminowo.</Li>
              <Li>Plany płatne rozliczane są w cyklu miesięcznym lub rocznym z góry. Szczegółowy cennik dostępny jest na stronie głównej.</Li>
              <Li>Płatności realizowane są przez zewnętrznego operatora płatności. Dane karty nie są przechowywane przez Usługodawcę.</Li>
              <Li>Zgodnie z art. 38 pkt 13 ustawy o prawach konsumenta, Użytkownik przyjmuje do wiadomości, że prawo odstąpienia od umowy o dostarczanie treści cyfrowych nie przysługuje po rozpoczęciu wykonywania usługi za wyraźną zgodą konsumenta.</Li>
              <Li>W przypadku braku płatności konto jest przełączane na plan Free. Dane są zachowane przez 90 dni, po czym mogą zostać usunięte.</Li>
            </ul>
          </Section>

          {/* § 6 */}
          <Section id="odpowiedzialnosc" title="§ 6. Odpowiedzialność i wyłączenia">
            <ul className="space-y-2">
              <Li>Platforma świadczona jest w stanie „takim, jaki jest" (as-is). Usługodawca nie gwarantuje nieprzerwanej dostępności usługi.</Li>
              <Li>Usługodawca nie ponosi odpowiedzialności za: (a) utratę danych spowodowaną działaniem siły wyższej; (b) treści publikowane przez Użytkowników; (c) decyzje Użytkownika podejmowane na podstawie informacji dostępnych na Platformie.</Li>
              <Li>Łączna odpowiedzialność Usługodawcy wobec Użytkownika nie przekracza kwoty opłat uiszczonych przez Użytkownika za ostatnie 12 miesięcy.</Li>
              <Li>Powyższe ograniczenia nie dotyczą szkód wyrządzonych umyślnie ani roszczeń konsumenckich wynikających z bezwzględnie obowiązujących przepisów prawa polskiego.</Li>
            </ul>
          </Section>

          {/* § 7 */}
          <Section id="rozwiazanie" title="§ 7. Rozwiązanie umowy i usunięcie konta">
            <ul className="space-y-2">
              <Li>Użytkownik może w każdej chwili usunąć konto z poziomu ustawień konta lub pisząc na adres {ADMIN_EMAIL}. Umowa ulega rozwiązaniu ze skutkiem natychmiastowym.</Li>
              <Li>Po usunięciu konta dane Użytkownika są anonimizowane lub trwale usuwane w ciągu 30 dni, z wyjątkiem danych wymaganych przepisami prawa (np. dane rozliczeniowe — 5 lat).</Li>
              <Li>Usługodawca może rozwiązać umowę z Użytkownikiem z 14-dniowym wyprzedzeniem w przypadku naruszenia Regulaminu lub zaprzestania działalności.</Li>
              <Li>W przypadku poważnego naruszenia Regulaminu (np. publikacja treści nielegalnych) konto może zostać zawieszone lub usunięte bez okresu wypowiedzenia.</Li>
            </ul>
          </Section>

          {/* § 8 */}
          <Section id="reklamacje" title="§ 8. Reklamacje">
            <ul className="space-y-2">
              <Li>Reklamacje dotyczące działania Platformy należy kierować na adres e-mail: <a href={`mailto:${ADMIN_EMAIL}`} className="underline underline-offset-2 hover:opacity-80" style={{ color: "rgba(212,175,55,0.65)" }}>{ADMIN_EMAIL}</a>.</Li>
              <Li>Reklamacja powinna zawierać: opis problemu, datę wystąpienia, adres e-mail konta.</Li>
              <Li>Usługodawca rozpatruje reklamacje w terminie 14 dni kalendarzowych od daty otrzymania.</Li>
              <Li>Konsument ma prawo skorzystać z pozasądowych metod rozwiązywania sporów, w szczególności platformy ODR: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-80" style={{ color: "rgba(212,175,55,0.65)" }}>ec.europa.eu/consumers/odr</a>.</Li>
            </ul>
          </Section>

          {/* § 9 */}
          <Section id="prawo" title="§ 9. Prawo właściwe i rozstrzyganie sporów">
            <ul className="space-y-2">
              <Li>Regulamin podlega prawu polskiemu.</Li>
              <Li>Spory między Usługodawcą a Użytkownikiem będącym konsumentem rozstrzygane są przez sąd właściwy dla miejsca zamieszkania konsumenta.</Li>
              <Li>Spory z Użytkownikami niebędącymi konsumentami rozstrzygane są przez sąd właściwy dla siedziby Usługodawcy.</Li>
            </ul>
          </Section>

          {/* § 10 */}
          <Section id="postanowienia" title="§ 10. Postanowienia końcowe">
            <ul className="space-y-2">
              <Li>Regulamin wchodzi w życie z dniem {LAST_UPDATED}.</Li>
              <Li>O zmianach Regulaminu Użytkownicy informowani są e-mailem z co najmniej 14-dniowym wyprzedzeniem. Brak sprzeciwu w tym terminie oznacza akceptację zmian.</Li>
              <Li>Jeżeli jakiekolwiek postanowienie Regulaminu zostanie uznane za nieważne, pozostałe postanowienia pozostają w mocy.</Li>
              <Li>Regulamin dostępny jest w wersji elektronicznej na stronie <strong style={{ color: "rgba(210,225,240,0.8)" }}>e-grob.pl/regulamin</strong> oraz do pobrania w formacie PDF na żądanie.</Li>
            </ul>
          </Section>

          {/* Nawigacja */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to={ROUTE_PATHS.POLITYKA}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all duration-200 hover:brightness-110"
              style={{ background: "rgba(212,175,55,0.85)", color: "rgba(6,8,18,0.95)" }}>
              Polityka prywatności <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to={ROUTE_PATHS.HOME}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm transition-all duration-200 hover:bg-white/5"
              style={{ border: "1px solid oklch(0.20 0.03 220)", color: "rgba(160,175,195,0.7)" }}>
              ← Wróć na stronę główną
            </Link>
          </div>

        </div>
      </div>
    </Layout>
  );
}
